/**
 * The mobile hand-off through /subscribe (PRD 87 §4, PRD 113 §5).
 *
 * A new mobile user verifies on the web and is sent to
 * /subscribe?return_url=<scheme>://auth-callback?access_token=…&refresh_token=…
 * (LoginForm). That URL signs the app in, so it is a credential:
 * - it is kept in sessionStorage for exactly the round trip through Stripe
 *   Checkout, which drops our query string, and nowhere else. It never goes to
 *   the api (which refuses it), Stripe, analytics or a log;
 * - it is followed only when it is one of the app's own deep links. Anything
 *   else stored under the key (a crafted link, an https URL) is dropped, so
 *   /subscribe cannot be used to bounce a person who has just paid to another
 *   site;
 * - and only when it signs the app in as the person signed in here
 *   (appReturnUrlFor). The web sells Pro to whoever is signed in on the web,
 *   so a link for anybody else would hand the app an account that did not
 *   pay, or sign the app into somebody else's account altogether. Logout
 *   forgets it too (AuthContext).
 *
 * The link's user_uuid is the only owner it states, and nothing signs it: the
 * tokens are opaque. So the check stops a link for the wrong account, the
 * mistake; it cannot stop a forged link that names the person's own uuid.
 */

export const RETURN_URL_SESSION_KEY = "subscribe_return_url";

/**
 * The app's deep-link schemes: LoginForm's default when the app sends no
 * return_scheme, and the production and alpha builds' own
 * (mobile/app.config.ts, deepLinkScheme).
 */
export const APP_SCHEMES: readonly string[] = ["greenback", "com.greenbackapp.mobile", "com.greenback.alpha"];

// Whitespace and control characters have no place in a deep link, and some
// parsers strip them, which is how "java\nscript:" style tricks get through.
// eslint-disable-next-line no-control-regex
const UNSAFE_CHARACTERS = /[\s\u0000-\u001f\u007f]/;
const SCHEME_PREFIX = /^([a-z][a-z0-9+.-]*):\/\//i;

export const isAppScheme = (scheme: string | null | undefined): scheme is string =>
  typeof scheme === "string" && APP_SCHEMES.includes(scheme.toLowerCase());

/** `raw` when it is one of the app's own deep links (`<app scheme>://…`), else null. */
export const safeAppReturnUrl = (raw: string | null | undefined): string | null => {
  if (typeof raw !== "string" || !raw || UNSAFE_CHARACTERS.test(raw)) return null;
  const match = SCHEME_PREFIX.exec(raw);
  if (!match || !isAppScheme(match[1])) return null;
  try {
    // Parses as a URL with that same scheme, so nothing in the rest of it
    // changes what it is.
    const parsed = new URL(raw);
    return parsed.protocol.toLowerCase() === `${match[1].toLowerCase()}:` ? raw : null;
  } catch {
    return null;
  }
};

/** The scheme of a valid app deep link, lowercased, or null. */
export const appSchemeOf = (raw: string | null | undefined): string | null => {
  const url = safeAppReturnUrl(raw);
  return url ? SCHEME_PREFIX.exec(url)![1].toLowerCase() : null;
};

const normalizeUuid = (uuid: string | null | undefined): string | null => {
  const trimmed = typeof uuid === "string" ? uuid.trim().toLowerCase() : "";
  return trimmed || null;
};

/** Whether two user uuids name the same account. Never true when either is missing. */
export const sameUser = (a: string | null | undefined, b: string | null | undefined): boolean => {
  const left = normalizeUuid(a);
  return left !== null && left === normalizeUuid(b);
};

/** The account a hand-off link signs the app in as (its user_uuid), or null when it names none. */
export const appLinkUserUuid = (raw: string | null | undefined): string | null => {
  const link = safeAppReturnUrl(raw);
  if (!link) return null;
  try {
    return normalizeUuid(new URL(link).searchParams.get("user_uuid"));
  } catch {
    return null;
  }
};

/**
 * `raw` when it is one of the app's own deep links AND signs the app in as
 * `userUuid`; else null. A link that names no account is never followed.
 */
export const appReturnUrlFor = (raw: string | null | undefined, userUuid: string | null | undefined): string | null => {
  const link = safeAppReturnUrl(raw);
  return link && sameUser(appLinkUserUuid(link), userUuid) ? link : null;
};

/** Keeps the return_url for the round trip. False when storage refused it. */
export const rememberReturnUrl = (raw: string): boolean => {
  try {
    sessionStorage.setItem(RETURN_URL_SESSION_KEY, raw);
    return sessionStorage.getItem(RETURN_URL_SESSION_KEY) === raw;
  } catch {
    return false;
  }
};

export const storedReturnUrl = (): string | null => {
  try {
    return sessionStorage.getItem(RETURN_URL_SESSION_KEY);
  } catch {
    return null;
  }
};

export const forgetReturnUrl = (): void => {
  try {
    sessionStorage.removeItem(RETURN_URL_SESSION_KEY);
  } catch {
    // Storage unavailable: nothing was kept.
  }
};
